import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Plus, Trash2, Download, ExternalLink } from "lucide-react";

export default function InvoicesPage() {
    const [invoices, setInvoices] = useState([]);
    const [trips, setTrips] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showPicker, setShowPicker] = useState(false);
    const navigate = useNavigate();

    const load = async () => {
        setLoading(true);
        try {
            const [inv, tr] = await Promise.all([
                api.get("/invoices"),
                api.get("/trips"),
            ]);
            setInvoices(inv.data || []);
            setTrips(tr.data || []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const createEmpty = async () => {
        try {
            const { data } = await api.post("/invoices", { client_name: "", title: "Neue Rechnung", items: [{ description: "", quantity: 1, unit_price: 0 }] });
            navigate(`/invoices/${data.id}`);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const createFromTrip = async (tripId) => {
        try {
            const { data } = await api.post(`/invoices/from-trip/${tripId}`);
            toast.success("Rechnung aus Reise erzeugt");
            navigate(`/invoices/${data.id}`);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Rechnung wirklich löschen?")) return;
        try {
            await api.delete(`/invoices/${id}`);
            await load();
            toast.success("Rechnung gelöscht");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const downloadPdf = async (inv) => {
        try {
            const res = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `Rechnung_${inv.invoice_number}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const fmtMoney = (v, c) => `${c || "CHF"} ${Number(v || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const fmtDate = (s) => {
        if (!s) return "—";
        try { const d = new Date(s); return d.toLocaleDateString("de-CH"); } catch { return s; }
    };

    return (
        <AppLayout>
            <div className="max-w-6xl">
                <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                    <div>
                        <p className="text-xs uppercase tracking-[0.18em] text-ink-tertiary mb-2">Buchhaltung</p>
                        <h1 className="text-3xl font-semibold text-ink">Rechnungen</h1>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setShowPicker(true)}
                            data-testid="invoice-from-trip-btn"
                            className="inline-flex items-center gap-2 bg-white border border-sand-300 text-ink rounded-md px-4 py-2 text-sm font-medium hover:border-navy transition-colors"
                        >
                            <FileText className="w-4 h-4" /> Aus Reise erstellen
                        </button>
                        <button
                            type="button"
                            onClick={createEmpty}
                            data-testid="invoice-new-btn"
                            className="inline-flex items-center gap-2 bg-terracotta text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-terracotta-dark transition-colors"
                        >
                            <Plus className="w-4 h-4" /> Neue Rechnung
                        </button>
                    </div>
                </div>

                {loading ? (
                    <p className="text-sm text-ink-tertiary">Laden …</p>
                ) : invoices.length === 0 ? (
                    <div className="bg-white border border-sand-300 rounded-md py-12 text-center">
                        <FileText className="w-10 h-10 text-ink-tertiary mx-auto mb-3" />
                        <p className="text-ink-secondary mb-1">Noch keine Rechnungen.</p>
                        <p className="text-xs text-ink-tertiary">Erstelle deine erste Rechnung — manuell oder direkt aus einer Reise.</p>
                    </div>
                ) : (
                    <div className="bg-white border border-sand-300 rounded-md overflow-hidden">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-ink text-white">
                                    <Th>Nr.</Th>
                                    <Th>Kunde</Th>
                                    <Th>Betreff</Th>
                                    <Th>Datum</Th>
                                    <Th right>Betrag</Th>
                                    <Th right>Aktionen</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoices.map((inv) => (
                                    <tr key={inv.id} className="border-b border-sand-300 hover:bg-sand-100/60 transition-colors" data-testid={`invoice-row-${inv.id}`}>
                                        <Td><Link to={`/invoices/${inv.id}`} className="font-semibold text-ink hover:underline">{inv.invoice_number || "—"}</Link></Td>
                                        <Td>{inv.client_name || <span className="text-ink-tertiary">—</span>}</Td>
                                        <Td className="max-w-xs truncate">{inv.title || <span className="text-ink-tertiary">—</span>}</Td>
                                        <Td>{fmtDate(inv.invoice_date)}</Td>
                                        <Td right className="font-semibold">{fmtMoney(inv.total, inv.currency)}</Td>
                                        <Td right>
                                            <div className="flex justify-end gap-1.5">
                                                <button onClick={() => downloadPdf(inv)} className="p-1.5 rounded hover:bg-sand-200 text-ink-secondary hover:text-navy transition-colors" title="PDF herunterladen" data-testid={`invoice-pdf-${inv.id}`}>
                                                    <Download className="w-4 h-4" />
                                                </button>
                                                <Link to={`/invoices/${inv.id}`} className="p-1.5 rounded hover:bg-sand-200 text-ink-secondary hover:text-navy transition-colors" title="Bearbeiten">
                                                    <ExternalLink className="w-4 h-4" />
                                                </Link>
                                                <button onClick={() => remove(inv.id)} className="p-1.5 rounded hover:bg-terracotta/10 text-ink-secondary hover:text-terracotta transition-colors" title="Löschen" data-testid={`invoice-delete-${inv.id}`}>
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </Td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {showPicker && (
                    <TripPicker trips={trips} onPick={(id) => { setShowPicker(false); createFromTrip(id); }} onClose={() => setShowPicker(false)} />
                )}
            </div>
        </AppLayout>
    );
}

function TripPicker({ trips, onPick, onClose }) {
    return (
        <div className="fixed inset-0 z-50 bg-ink/60 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-sand-100 max-w-2xl w-full max-h-[80vh] overflow-y-auto rounded-md shadow-xl p-6" onClick={(e) => e.stopPropagation()} data-testid="trip-picker">
                <h2 className="text-xl font-semibold text-ink mb-4">Reise wählen</h2>
                {trips.length === 0 ? (
                    <p className="text-sm text-ink-tertiary">Keine Reisen vorhanden.</p>
                ) : (
                    <div className="space-y-2">
                        {trips.map((t) => (
                            <button key={t.id} type="button" onClick={() => onPick(t.id)}
                                data-testid={`pick-trip-${t.id}`}
                                className="w-full text-left bg-white border border-sand-300 hover:border-navy hover:bg-sand-50 rounded-md px-4 py-3 transition-colors">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className="font-semibold text-ink truncate">{t.title || "—"}</p>
                                        <p className="text-xs text-ink-tertiary truncate">{t.client_name || "—"} · {t.destination || "—"}</p>
                                    </div>
                                    <span className="text-sm font-semibold text-ink whitespace-nowrap">{t.currency || "CHF"} {Number(t.total_cost || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </div>
                            </button>
                        ))}
                    </div>
                )}
                <div className="mt-4 flex justify-end">
                    <button type="button" onClick={onClose} className="text-sm text-ink-secondary hover:text-ink px-3 py-2">Abbrechen</button>
                </div>
            </div>
        </div>
    );
}

function Th({ children, right }) {
    return <th className={`px-4 py-3 text-[10px] font-bold uppercase tracking-[0.16em] ${right ? "text-right" : "text-left"}`}>{children}</th>;
}
function Td({ children, right, className = "" }) {
    return <td className={`px-4 py-3 text-ink ${right ? "text-right" : ""} ${className}`}>{children}</td>;
}
